import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Plus, ListChecks, ArrowRight } from 'lucide-react'
import { todoApi } from '@/services/api'
import { cn } from '@/lib/utils'

interface Todo {
  id: string
  text: string
  completed: boolean
  priority: string
  dueDate: string | null
  assignedTo: { firstName: string; lastName: string } | null
  createdBy: { id: string; firstName: string } | null
  userId: string
}

const PRIORITY_DOT: Record<string, string> = {
  LOW: 'bg-slate-300',
  NORMAL: 'bg-primary-500',
  HIGH: 'bg-warning-500',
  URGENT: 'bg-danger-500',
}

export function TodoWidget() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [newText, setNewText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Stessa queryKey della pagina To-Do (['todos', filter]) così le mutazioni
  // di entrambe le viste si sincronizzano: invalidateQueries(['todos']) le copre tutte.
  const { data: todosRaw } = useQuery({
    queryKey: ['todos', 'active'],
    queryFn: async () => {
      const res = await todoApi.list('active')
      const r = res.data as any
      return (r?.data ?? r ?? []) as Todo[]
    },
    staleTime: 30_000,
  })

  const todos = todosRaw ?? []

  const createMutation = useMutation({
    mutationFn: (text: string) => todoApi.create({ text }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] })
      queryClient.invalidateQueries({ queryKey: ['todos-count'] })
      setNewText('')
      inputRef.current?.focus()
    },
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => todoApi.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] })
      queryClient.invalidateQueries({ queryKey: ['todos-count'] })
    },
  })

  const handleSubmit = () => {
    if (!newText.trim()) return
    createMutation.mutate(newText.trim())
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-primary-600" />
          <h3 className="text-sm font-semibold text-slate-900">To-Do</h3>
          {todos.length > 0 && (
            <span className="text-[10px] font-semibold bg-primary-50 text-primary-600 px-1.5 py-0.5 rounded-full tabular-nums">
              {todos.length}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/todo')}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          Vedi tutti
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* Quick add */}
      <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-200">
        <Plus className="w-3.5 h-3.5 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder="Aggiungi task veloce..."
          className="flex-1 text-xs outline-none placeholder-slate-400 bg-transparent"
        />
        {newText.trim() && (
          <button
            onClick={handleSubmit}
            className="text-[10px] px-2 py-0.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Aggiungi
          </button>
        )}
      </div>

      {/* Todo list */}
      <div className="divide-y divide-slate-200 flex-1 min-h-0 overflow-y-auto">
        {todos.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-xs text-slate-400">Nessun task da fare</p>
          </div>
        ) : (
          todos.map((todo) => (
            <div key={todo.id} className="flex items-center gap-2.5 px-5 py-2 hover:bg-slate-50 transition-colors">
              <button
                onClick={() => toggleMutation.mutate(todo.id)}
                className={cn(
                  'flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
                  todo.completed ? 'bg-success-500 border-success-500' : 'border-slate-300 hover:border-primary-400'
                )}
              >
                {todo.completed && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </button>
              <span className={cn('flex-1 text-xs truncate', todo.completed ? 'line-through text-slate-400' : 'text-slate-700')}>
                {todo.text}
              </span>
              <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', PRIORITY_DOT[todo.priority] || PRIORITY_DOT.NORMAL)} />
              {todo.assignedTo && (
                <span className="text-[9px] text-slate-500 flex-shrink-0">→ {todo.assignedTo.firstName}</span>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
